/**
 * Shared suggestion renderer for TipTap mention/tag dropdowns.
 *
 * Creates a React portal that mounts the SuggestionList component
 * into a floating DOM element positioned near the cursor.
 */

import { createRoot, type Root } from 'react-dom/client';
import type { SuggestionOptions, SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion';
import { SuggestionList, type SuggestionItem, type SuggestionListRef } from './SuggestionList';

interface CreateRendererOptions {
  variant: 'mention' | 'tag' | 'entity';
}

export function createSuggestionRenderer(
  opts: CreateRendererOptions,
): NonNullable<SuggestionOptions<SuggestionItem>['render']> {
  return () => {
    let container: HTMLDivElement | null = null;
    let root: Root | null = null;
    let componentRef: SuggestionListRef | null = null;

    return {
      onStart(props: SuggestionProps<SuggestionItem>) {
        container = document.createElement('div');
        container.classList.add('suggestion-container');
        document.body.appendChild(container);

        root = createRoot(container);

        root.render(
          <SuggestionList
            ref={(ref) => {
              componentRef = ref;
            }}
            items={props.items}
            command={props.command}
            variant={opts.variant}
          />,
        );

        positionContainer(container, props.clientRect);
      },

      onUpdate(props: SuggestionProps<SuggestionItem>) {
        if (!root || !container) return;

        root.render(
          <SuggestionList
            ref={(ref) => {
              componentRef = ref;
            }}
            items={props.items}
            command={props.command}
            variant={opts.variant}
          />,
        );

        positionContainer(container, props.clientRect);
      },

      onKeyDown(props: SuggestionKeyDownProps) {
        if (props.event.key === 'Escape') {
          destroy();
          return true;
        }
        return componentRef?.onKeyDown(props) ?? false;
      },

      onExit() {
        destroy();
      },
    };

    function destroy() {
      // Defer unmount to avoid React "flushSync" warnings
      if (root) {
        const r = root;
        root = null;
        setTimeout(() => r.unmount(), 0);
      }
      if (container) {
        const c = container;
        container = null;
        setTimeout(() => c.remove(), 0);
      }
      componentRef = null;
    }
  };
}

// ---------- Positioning helper ----------

function positionContainer(
  el: HTMLDivElement,
  clientRect: SuggestionProps['clientRect'],
) {
  if (!clientRect) return;
  const rect = typeof clientRect === 'function' ? clientRect() : clientRect;
  if (!rect) return;

  el.style.position = 'fixed';
  el.style.zIndex = '9999';

  // Position below the cursor, with some offset
  const top = rect.bottom + 4;
  const left = rect.left;

  // Check if dropdown would go off the bottom of viewport
  const viewportHeight = window.innerHeight;
  const dropdownMaxHeight = 280; // matches max-height in CSS

  if (top + dropdownMaxHeight > viewportHeight && rect.top > dropdownMaxHeight) {
    // Position above the cursor instead
    el.style.top = `${rect.top - 4}px`;
    el.style.left = `${left}px`;
    el.style.transform = 'translateY(-100%)';
  } else {
    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
    el.style.transform = 'none';
  }
}
